(function () {
  const CART_UPDATE_EVENT = 'cart:update';
  const CART_ERROR_EVENT = 'cart:error';
  const ADDED_DURATION = 1500;

  class BundlesBlock extends HTMLElement {
    constructor() {
      super();
      this.tiers = [];
      this.cart = { item_count: 0, items_subtotal_price: 0 };
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.parseConfig();
      this.setTierCount();
      this.bindRows();
      this.fetchCart();
      document.addEventListener(CART_UPDATE_EVENT, this.onCartUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener(CART_UPDATE_EVENT, this.onCartUpdate);
    }

    parseConfig() {
      try {
        const raw = this.getAttribute('data-tiers') || '[]';
        this.tiers = JSON.parse(raw)
          .filter((t) => Number.isFinite(t.min) && Number.isFinite(t.percent))
          .sort((a, b) => a.min - b.min);
      } catch (err) {
        this.tiers = [];
      }
      this.progressTemplate = this.getAttribute('data-progress-template') || '';
      this.reachedTemplate = this.getAttribute('data-reached-template') || '';
      this.topReachedTemplate = this.getAttribute('data-top-reached-template') || '';
    }

    setTierCount() {
      this.style.setProperty('--tier-count', String(this.tiers.length || 1));
    }

    bindRows() {
      this.querySelectorAll('[data-bundles-row]').forEach((row) => {
        row.addEventListener('submit', (e) => this.handleSubmit(e, row));

        row.querySelectorAll('[data-qty-step]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const step = parseInt(btn.getAttribute('data-qty-step'), 10) || 1;
            const input = row.querySelector('[data-bundles-qty]');
            if (!input) return;
            const current = parseInt(input.value, 10) || 1;
            const next = Math.max(1, current + step);
            input.value = String(next);
          });
        });

        const sizeSelect = row.querySelector('[data-bundles-size]');
        const priceEl = row.querySelector('[data-bundles-price]');
        if (sizeSelect && priceEl && !priceEl.hasAttribute('data-from-price')) {
          sizeSelect.addEventListener('change', () => {
            const opt = sizeSelect.options[sizeSelect.selectedIndex];
            const priceCents = opt && opt.getAttribute('data-price');
            if (priceCents == null) return;
            const priceSpan = priceEl.querySelector('.price') || priceEl;
            priceSpan.textContent = formatMoney(parseInt(priceCents, 10));
          });
        }
      });
    }

    async fetchCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        this.cart = cart;
        this.render();
      } catch (err) {
        /* silent — render will run with default zero state */
      }
    }

    onCartUpdate(e) {
      // Theme dispatches cart:update with detail.resource (the cart) or detail.data
      const detail = e && e.detail;
      const resource = detail && (detail.resource || detail.cart);
      if (resource && typeof resource.item_count === 'number') {
        this.cart = resource;
        this.render();
      } else {
        // Fall back to fetching fresh cart
        this.fetchCart();
      }
    }

    getCurrentTier() {
      let current = null;
      for (const tier of this.tiers) {
        if (this.cart.item_count >= tier.min) current = tier;
      }
      return current;
    }

    getNextTier() {
      for (const tier of this.tiers) {
        if (this.cart.item_count < tier.min) return tier;
      }
      return null;
    }

    render() {
      this.renderTiers();
      this.renderProgress();
      this.renderFooter();
    }

    renderTiers() {
      this.querySelectorAll('[data-tier-min]').forEach((el) => {
        const min = parseInt(el.getAttribute('data-tier-min'), 10);
        el.classList.toggle('is-reached', this.cart.item_count >= min);
      });

      const next = this.getNextTier();
      this.querySelectorAll('[data-tier-min]').forEach((el) => {
        const min = parseInt(el.getAttribute('data-tier-min'), 10);
        el.classList.toggle('is-next', next && next.min === min);
      });
    }

    renderProgress() {
      const progressEl = this.querySelector('[data-bundles-progress]');
      if (!progressEl) return;

      const current = this.getCurrentTier();
      const next = this.getNextTier();

      let html = '';
      if (next) {
        const countToGo = next.min - this.cart.item_count;
        html = fillTemplate(this.progressTemplate, {
          count: '<b>' + countToGo + '</b>',
          percent: '<b>' + next.percent + '</b>',
        });
      } else if (current) {
        html = fillTemplate(this.topReachedTemplate, {
          percent: '<b>' + current.percent + '</b>',
        });
      }

      progressEl.innerHTML = html;
    }

    renderFooter() {
      const footer = this.querySelector('[data-bundles-footer]');
      if (!footer) return;

      if (this.cart.item_count === 0) {
        footer.hidden = true;
        return;
      }

      footer.hidden = false;

      const totalEl = footer.querySelector('[data-bundles-total]');
      if (totalEl) {
        const itemsLabel = this.cart.item_count === 1 ? 'item' : 'items';
        totalEl.textContent =
          this.cart.item_count + ' ' + itemsLabel + ' in cart · ' + formatMoney(this.cart.items_subtotal_price);
      }

      const savingEl = footer.querySelector('[data-bundles-saving]');
      if (savingEl) {
        const tier = this.getCurrentTier();
        if (tier && tier.percent > 0) {
          const saved = Math.round((this.cart.items_subtotal_price * tier.percent) / 100);
          savingEl.textContent = 'Saving ' + formatMoney(saved);
        } else {
          savingEl.textContent = '';
        }
      }
    }

    async handleSubmit(e, row) {
      e.preventDefault();

      const button = row.querySelector('[data-bundles-add]');
      if (!button || button.disabled) return;

      button.disabled = true;
      button.classList.remove('is-added');

      const formData = new FormData(row);
      // Normalise quantity (strip whitespace, fallback to 1)
      const qty = parseInt(formData.get('quantity'), 10);
      formData.set('quantity', String(Number.isFinite(qty) && qty > 0 ? qty : 1));

      try {
        const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
        const res = await fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add item');
        }

        await res.json();

        // Fire cart:update — the theme listens for this and will refresh the drawer.
        // Also fetch a fresh cart so our own render is in sync.
        await this.fetchCart();
        document.dispatchEvent(
          new CustomEvent(CART_UPDATE_EVENT, {
            bubbles: true,
            detail: {
              resource: this.cart,
              sourceId: 'bundles',
              data: { source: 'bundles' },
            },
          })
        );

        this.showAdded(button);
      } catch (err) {
        document.dispatchEvent(
          new CustomEvent(CART_ERROR_EVENT, {
            bubbles: true,
            detail: { sourceId: 'bundles', error: err && err.message },
          })
        );
        button.disabled = false;
      }
    }

    showAdded(button) {
      button.classList.add('is-added');
      setTimeout(() => {
        button.classList.remove('is-added');
        button.disabled = false;
      }, ADDED_DURATION);
    }
  }

  function fillTemplate(tpl, vars) {
    if (!tpl) return '';
    return tpl.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : ''));
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  if (!customElements.get('bundles-block')) {
    customElements.define('bundles-block', BundlesBlock);
  }
})();
